import * as Sentry from '@sentry/react'

// Mirror of creator-app/services/sentry.ts, adapted for the web (Vite + React).
// SDK init is a no-op when VITE_SENTRY_DSN is empty/unset, so this file is
// safe to ship before the DSN is provisioned.

const SENTRY_ENABLED = true
const SENTRY_DSN = (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
const APP_ENV = (import.meta.env.VITE_ENV ?? import.meta.env.MODE ?? 'development') as
  'development' | 'staging' | 'production'
const isSentryEnabled = SENTRY_ENABLED && Boolean(SENTRY_DSN)

// Phase 2 per-environment sampling table.
const SAMPLE = (
  APP_ENV === 'production' ? { traces: 0.2, session: 0.1, error: 1.0, profiles: 0.1, debug: false } :
  APP_ENV === 'staging'    ? { traces: 1.0, session: 0.5, error: 1.0, profiles: 0.5, debug: false } :
                             { traces: 1.0, session: 0.0, error: 1.0, profiles: 1.0, debug: true }
)

// Distributed tracing — only propagate sentry-trace/baggage to our own worker.
// Add more brand-owned API domains here as they appear.
const PROPAGATE_TARGETS: (string | RegExp)[] = [
  'bmc-intel-worker.mikerama94.workers.dev',
  /^\/api\//,
]

export function initializeSentry(): void {
  if (!isSentryEnabled) {
    if (import.meta.env.DEV) console.log('[Sentry] disabled — no VITE_SENTRY_DSN configured')
    return
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
      debug: SAMPLE.debug,
      tracesSampleRate: SAMPLE.traces,
      // Browser profiling only COLLECTS samples when the document is served
      // with the response header `Document-Policy: js-profiling`. Without it
      // this is a silent no-op (no crash) — see needs_followup re: the
      // Cloudflare Pages `_headers` file / response header.
      profilesSampleRate: SAMPLE.profiles,
      replaysSessionSampleRate: SAMPLE.session,
      replaysOnErrorSampleRate: SAMPLE.error,
      tracePropagationTargets: PROPAGATE_TARGETS,
      enabled: !import.meta.env.DEV || import.meta.env.VITE_ENABLE_SENTRY_IN_DEV === 'true',
      attachStacktrace: true,
      sendDefaultPii: false,
      // Non-actionable noise, almost entirely from /invite/* links opened by
      // email-security scanners and browsers with extensions (0 real users):
      // - "Object Not Found Matching Id": Microsoft Outlook SafeLinks scanner
      //   artifact injected into the page it crawls
      // - runtime.sendMessage / "Tab not found": browser-extension internals
      // - Navigator LockManager "steal"/timeout: supabase-js auth lock
      //   contention between multiple tabs — benign, auth recovers
      ignoreErrors: [
        /Object Not Found Matching Id/,
        /runtime\.sendMessage/,
        /Lock broken by another request/,
        /Navigator LockManager/,
        /ResizeObserver loop/,
      ],
      integrations: [
        Sentry.browserTracingIntegration(),
        // Browser Profiling — pairs with `profilesSampleRate` above. Only
        // collects when the document is served with the response header
        // `Document-Policy: js-profiling`; degrades silently without it.
        Sentry.browserProfilingIntegration(),
        // Session Replay with privacy-by-default. Selective unmasking
        // can be wired later via `mask`/`unmask` classNames on specific
        // safe UI elements (e.g. headers, public counters).
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
        // No feedbackIntegration — the auto-injected "Report a Bug" button was
        // removed on 2026-09-08. Re-adding it means re-adding the integration
        // here (use `autoInject: false` + a manual trigger if it comes back).
      ],
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.level === 'debug') return null
        if (breadcrumb.category?.startsWith('ui.click')) return null
        return breadcrumb
      },
      beforeSend(event, hint) {
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(b => {
            if (b.data) {
              delete b.data.email
              delete b.data.password
              delete b.data.token
              delete b.data.access_token
            }
            return b
          })
        }
        if (hint?.originalException) {
          const err = hint.originalException as Error
          if (err?.message?.toLowerCase().includes('network request failed')) return null
        }
        return event
      },
    })
    Sentry.setTag('app', 'brand-dashboard')
    Sentry.setTag('app_env', APP_ENV)
    if (import.meta.env.DEV) console.log('[Sentry] initialized')
  } catch (err) {
    console.error('[Sentry] init failed:', err)
  }
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!isSentryEnabled) return
  try {
    if (context) {
      Sentry.withScope(scope => {
        scope.setContext('custom', context)
        Sentry.captureException(error)
      })
    } else {
      Sentry.captureException(error)
    }
  } catch (e) {
    console.error('[Sentry] captureException failed:', e)
  }
}

export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!isSentryEnabled) return
  try {
    Sentry.captureMessage(message, level)
  } catch (e) {
    console.error('[Sentry] captureMessage failed:', e)
  }
}

export function setUser(user: { id: string; email?: string } | null): void {
  if (!isSentryEnabled) return
  try {
    Sentry.setUser(user ? { id: user.id, email: user.email } : null)
  } catch (e) {
    console.error('[Sentry] setUser failed:', e)
  }
}

export function setBrand(brand: { id: string; brand_name?: string } | null): void {
  if (!isSentryEnabled) return
  try {
    if (brand) {
      Sentry.setTag('brand_id', brand.id)
      if (brand.brand_name) Sentry.setTag('brand_name', brand.brand_name)
    } else {
      Sentry.setTag('brand_id', '')
      Sentry.setTag('brand_name', '')
    }
  } catch (e) {
    console.error('[Sentry] setBrand failed:', e)
  }
}

export function isSentryConfigured(): boolean {
  return isSentryEnabled
}

export { Sentry }
